/**
 * Hardanger charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hardangerChartsPack: ToilePack = {
    slug: 'hardanger-charts',
    category: 'craft',
    name: 'Hardanger charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'square', label: 'Square motif', subject: 'a Hardanger chart of a square motif, kloster blocks of five satin stitches outlining a cut centre, the cut threads marked in a lighter tone and woven bars across the open squares'},
        {key: 'eyes', label: "Dove's eyes", subject: "a Hardanger chart of a cut grid with a dove's eye filling in each open square and the kloster blocks forming a diamond around them"},
        {key: 'border', label: 'Border', subject: 'a Hardanger chart of a stepped border for a table runner, kloster blocks climbing in steps and small eyelets between them'},
    ],
    styles: [
        {key: 'stitches', label: 'Stitch chart', style: 'thin black stitch lines on a faint counting grid, satin blocks drawn as parallel lines and cut areas left empty', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'cutaway', label: 'Cut areas', style: 'satin blocks in solid black and the cut areas shaded light grey so the openwork reads at a glance', palette: 'black blocks and light grey cut areas on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'blue', label: 'Two tone', style: 'satin blocks in black and the cut and filling stitches in a pale blue line', palette: 'black and pale blue line on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['snowflake', 'bookmark', 'table runner'],
    directionHint: 'try: snowflake',
    seo: {
        title: "Hardanger charts: square motif, dove's eyes, border",
        metaDescription: "Nine Hardanger charts: square motifs, dove's eye fillings and a stepped border, showing satin blocks, cut areas and fillings.",
        h1: "Hardanger charts: a square motif, dove's eyes and a border",
        tagline: 'Counted charts of satin blocks, cut areas and the filling stitches that hold the lace grid.',
        intro: "Hardanger is Norwegian white-on-white openwork: satin-stitch blocks, with threads cut and drawn out between them to leave a lace grid. A counted chart shows the blocks, cut areas and fillings. These nine cover a square motif, a dove's eye filling and a stepped border. Each comes as a stitch chart, a chart with cut areas shaded, or a two-tone chart. Type a different piece and the same layout carries it.",
        useCases: [
            'a table runner border',
            'a snowflake or square motif',
            'a bookmark',
            'a cloth with openwork edges',
        ],
        faq: [
            {q: 'What is a kloster block?', a: 'A block of satin stitches worked close together. The threads between blocks are cut away.'},
            {q: 'Which chart shows the cut areas?', a: 'The cut areas style, where the satin blocks are solid black and the cut areas are shaded light grey.'},
            {q: 'Can I use a different motif?', a: 'Yes. Type the motif you want and the same counted layout carries it.'},
        ],
    },
}
